import type { DefaultTemplateRootProps, RepeaterFunctionInvoker, RepeaterRow } from 'Components/types';
import createRepeaterComponents from 'Utils/repeater';

import SearchItemData, { type SearchItemRawData } from './searchItemData';

export default class SearchItemDataCollection {
  _items: SearchItemData[];

  constructor(responseItems: SearchItemRawData[] = [], type: string = '') {
    this._items = responseItems.map((item, index) => new SearchItemData(item, type, index));
  }

  get items(): SearchItemData[] {
    return this._items;
  }

  get length(): number {
    return this._items.length;
  }

  concat(other: SearchItemDataCollection): SearchItemDataCollection {
    const { _items: newItems } = other;
    if (!newItems.length) {
      return this;
    }

    const { _items: items } = this;

    if (items.length) {
      const [{ type }] = items;
      const [{ type: typeOfNew }] = newItems;
      if (type !== typeOfNew) {
        console.error('trying to concat SearchItemDataCollections with different types:', type, typeOfNew);
      }
    }

    const newCollection = new SearchItemDataCollection();
    newCollection._items = [...items, ...newItems];
    return newCollection;
  }

  find(predicate: (item: SearchItemData) => boolean): SearchItemData | undefined {
    return this._items.find(predicate);
  }

  map<T>(predicate: (item: SearchItemData, index: number) => T): T[] {
    return this._items.map(predicate);
  }

  reduce<T>(predicate: (acc: T, item: SearchItemData) => T, initialValue: T): T {
    return this._items.reduce(predicate, initialValue);
  }

  equals(other: SearchItemDataCollection): boolean {
    const { _items: items } = this;
    const { _items: newItems } = other;
    // order is important too
    return items.length === newItems.length && items.every(({ key }, i) => key === newItems[i].key);
  }

  indexOf(item: SearchItemData): number {
    return this._items.indexOf(item);
  }

  some(cb: (item: SearchItemData, index: number) => boolean, thisArg?: unknown): boolean {
    return this._items.some(cb, thisArg);
  }

  repeaterComponents<
    Params,
    RootProps extends object = DefaultTemplateRootProps,
    RootElement extends HTMLElement = HTMLElement,
  >(
    Component: RepeaterRow<Params, SearchItemData, 'item', RootProps, RootElement>,
  ): RepeaterFunctionInvoker<Params, RootProps, RootElement> {
    return createRepeaterComponents(this._items, 'item', Component, (item) => String(item.key));
  }
}
